import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { cardsApi } from '@/api/cardsApi'
import type { CardProgressRecord, CardProgressType } from '@/types'

const PROGRESS_TYPE_LABELS: Record<CardProgressType, string> = {
  translation: 'cards.translation_progress',
  pronunciation: 'cards.pronunciation_progress',
  spelling: 'cards.spelling_progress',
  reverse_translation: 'cards.reverse_translation_progress',
  listening: 'cards.listening_progress',
}

interface CardProgressHistoryProps {
  cardId: number
}

export function CardProgressHistory({ cardId }: CardProgressHistoryProps) {
  const { t } = useTranslation()
  const [items, setItems] = useState<CardProgressRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [filterType, setFilterType] = useState<CardProgressType | ''>('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    cardsApi
      .getCardProgressHistory(cardId, {
        order_by: 'created_at',
        order_direction: 'desc',
        per_page: rowsPerPage,
        page: page + 1,
      })
      .then((res) => {
        if (!cancelled) {
          setItems(res.data)
          setTotal(res.meta.total)
        }
      })
      .catch(() => {
        if (!cancelled) setItems([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [cardId, page, rowsPerPage])

  const filteredItems =
    filterType === ''
      ? items
      : items.filter((i) => i.progress_type === filterType)

  const hasHistory = total > 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">{t('cards.progress_history')}</Typography>

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Skeleton variant="rectangular" height={40} sx={{ maxWidth: 200, borderRadius: 1 }} />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('cards.filter.type')}</TableCell>
                  <TableCell align="right">{t('cards.change')}</TableCell>
                  <TableCell>{t('cards.reason')}</TableCell>
                  <TableCell>{t('cards.date')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                    <TableCell align="right"><Skeleton variant="text" width={32} sx={{ mx: 'auto' }} /></TableCell>
                    <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                    <TableCell><Skeleton variant="text" width={120} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Skeleton variant="rectangular" height={52} sx={{ borderRadius: 1 }} />
        </Box>
      ) : !hasHistory ? (
        <Typography variant="body2" color="text.secondary">
          {t('cards.progress_history_empty')}
        </Typography>
      ) : (
        <>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('cards.filter.type')}</InputLabel>
            <Select
              value={filterType}
              label={t('cards.filter.type')}
              onChange={(e) => setFilterType((e.target.value as CardProgressType) || '')}
            >
              <MenuItem value="">{t('cards.filter.all')}</MenuItem>
              {(Object.keys(PROGRESS_TYPE_LABELS) as CardProgressType[]).map(
                (key) => (
                  <MenuItem key={key} value={key}>
                    {t(PROGRESS_TYPE_LABELS[key])}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('cards.filter.type')}</TableCell>
                  <TableCell align="right">{t('cards.change')}</TableCell>
                  <TableCell>{t('cards.reason')}</TableCell>
                  <TableCell>{t('cards.date')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredItems.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {t(PROGRESS_TYPE_LABELS[row.progress_type])}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        component="span"
                        sx={{
                          color:
                            row.changed >= 0
                              ? 'success.main'
                              : 'error.main',
                          fontWeight: 500,
                        }}
                      >
                        {row.changed >= 0 ? '+' : ''}
                        {row.changed}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.reason}</TableCell>
                    <TableCell>
                      {new Date(row.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage={t('common.view')}
          />
        </>
      )}
    </Box>
  )
}
